import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, CheckCircle2, Clock, FileText, History, KeyRound, Loader2, Pencil, RefreshCw, Server, ShieldCheck, Trash2 } from 'lucide-react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  licensingApi,
  type LicensingStationRecord,
  type StationAuditRecord,
  type StationCertRecord,
  type StationOverrideRecord,
} from '@/api/licensing';
import { clientDetailApi } from '@/api/client-detail';
import { Header } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  getStationLifecycleDisplayState,
  getStationLicenseDisplayState,
  stationAuditEventLabel,
  stationAuditPayloadPreview,
} from './licensingViewModel';

function formatDateTime(value?: string | null): string {
  if (!value) return 'нет данных';
  return new Date(value).toLocaleString('ru-RU');
}

function statusBadge(station?: LicensingStationRecord) {
  if (!station) return 'outline';
  if (station.status === 'HEALTHY' && !station.outdated) return 'visit';
  if (station.status === 'SUSPENDED' || station.status === 'DISABLED') return 'deadline';
  return 'outline';
}

function getLicensingBackTo(state: unknown): string {
  if (state && typeof state === 'object' && 'licensingBackTo' in state) {
    const value = (state as { licensingBackTo?: unknown }).licensingBackTo;
    if (typeof value === 'string' && value.startsWith('/')) {
      return value;
    }
  }
  return '/more/licensing';
}

export function LicensingStationDetailPage() {
  const { stationId } = useParams<{ stationId: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [overrideReason, setOverrideReason] = useState('Разрешено администратором на время обслуживания');
  const [decommissionReason, setDecommissionReason] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteReason, setNoteReason] = useState('');

  const station = useQuery({
    queryKey: ['licensing', 'station', stationId],
    queryFn: () => licensingApi.getStation(stationId!),
    enabled: !!stationId,
  });
  const licenseState = useQuery({
    queryKey: ['licensing', 'station-license-state', stationId],
    queryFn: () => licensingApi.getStationLicenseState(stationId!),
    enabled: !!stationId,
  });
  const certs = useQuery({
    queryKey: ['licensing', 'station-certs', stationId],
    queryFn: () => licensingApi.listStationCerts(stationId!),
    enabled: !!stationId && !!station.data,
  });
  const overrides = useQuery({
    queryKey: ['licensing', 'station-overrides', stationId],
    queryFn: () => licensingApi.listStationOverrides(stationId!),
    enabled: !!stationId && !!station.data,
  });
  const audit = useQuery({
    queryKey: ['licensing', 'station-audit', stationId],
    queryFn: () => licensingApi.listStationAudit(stationId!),
    enabled: !!stationId && !!station.data,
  });
  const stationClient = useQuery({
    queryKey: ['client-detail', station.data?.clientId],
    queryFn: () => clientDetailApi.getMobileDetail(station.data!.clientId),
    enabled: !!station.data?.clientId,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['licensing'] });
  };

  const override = useMutation({
    mutationFn: () => licensingApi.issueStationOverride(stationId!, overrideReason.trim()),
    onSuccess: () => {
      toast.success('Разрешение на 24 часа выдано');
      invalidate();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Не удалось выдать разрешение'),
  });

  const decommission = useMutation({
    mutationFn: () => licensingApi.decommissionStation(stationId!, decommissionReason.trim()),
    onSuccess: () => {
      toast.success('Станция выведена из эксплуатации');
      invalidate();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Не удалось вывести станцию'),
  });

  const updateNote = useMutation({
    mutationFn: () => licensingApi.updateStationNote(stationId!, noteDraft, noteReason.trim()),
    onSuccess: (updated) => {
      queryClient.setQueryData(['licensing', 'station', stationId], updated);
      toast.success('Комментарий обновлён');
      setNoteOpen(false);
      setNoteReason('');
      invalidate();
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : 'Не удалось обновить комментарий'),
  });

  const stationData = station.data;
  const lifecycleDisplay = getStationLifecycleDisplayState(stationData);
  const licenseDisplay = getStationLicenseDisplayState({
    state: licenseState.data,
    isLoading: licenseState.isLoading,
  });
  const licenseData = licenseState.data;
  const backTo = getLicensingBackTo(location.state);
  const stationClientName = stationClient.data?.client.name || '';
  const openNoteEditor = () => {
    setNoteDraft(stationData?.policy?.notes ?? '');
    setNoteReason('');
    setNoteOpen(true);
  };

  return (
    <div className="min-h-full bg-background">
      <Header title="Станция" showBack backTo={backTo} />
      <main className="p-4 space-y-4">
        {station.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-36 w-full" />
          </div>
        )}

        {station.isError && (
          <section className="rounded-lg border bg-card p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
              <div>
                <h2 className="font-medium">Станция недоступна</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Проверьте доступ к клиенту или повторите попытку позже.
                </p>
              </div>
            </div>
          </section>
        )}

        {stationData && (
          <>
            <section className="rounded-lg border bg-card p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Server className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-medium">{stationData.hostname || 'Без hostname'}</h2>
                    <Badge variant={statusBadge(stationData)}>{stationData.status}</Badge>
                    {stationData.outdated && <Badge variant="outline">outdated</Badge>}
                  </div>
                  <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{stationData.id}</p>
                </div>
              </div>

              <dl className="mt-4 grid gap-3 text-sm">
                <InfoRow label="Клиент" value={clientDisplay(stationData.clientId, stationClientName)} />
                <InfoRow label="Приложение" value={stationData.appId} />
                <InfoRow label="Installation ID" value={stationData.installationId} mono />
                {stationData.policy?.notes && <InfoRow label="Комментарий" value={stationData.policy.notes} />}
                <InfoRow label="Версия" value={stationData.currentVersion || 'нет данных'} />
                <InfoRow label="Минимальная версия" value={stationData.minSupportedVersion || 'не задана'} />
                <InfoRow label="Платформа" value={`${stationData.os}/${stationData.arch}`} />
                <InfoRow label="Канал" value={stationData.releaseChannel || 'STABLE'} />
                <InfoRow label="Зарегистрирована" value={formatDateTime(stationData.enrolledAt)} />
                <InfoRow label="Последний контакт" value={formatDateTime(stationData.lastSeenAt)} />
                {stationData.decommissionedAt && (
                  <InfoRow label="Выведена" value={formatDateTime(stationData.decommissionedAt)} />
                )}
              </dl>
              {!stationData.decommissionedAt && (
                <Button type="button" variant="outline" className="mt-4 w-full" onClick={openNoteEditor}>
                  <Pencil className="h-4 w-4" />
                  {stationData.policy?.notes ? 'Редактировать комментарий' : 'Добавить комментарий'}
                </Button>
              )}
            </section>

            <Sheet open={noteOpen} onOpenChange={setNoteOpen}>
              <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
                <SheetHeader className="text-left">
                  <SheetTitle>Комментарий к станции</SheetTitle>
                  <SheetDescription>
                    Комментарий сохраняется в карточке станции. Причина изменения попадёт в audit.
                  </SheetDescription>
                </SheetHeader>
                <div className="mt-4 grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="station-note">Комментарий</Label>
                    <Textarea
                      id="station-note"
                      value={noteDraft}
                      maxLength={2000}
                      onChange={(event) => setNoteDraft(event.target.value)}
                      placeholder="бухгалтерия, ПК Ивановой, сервер mail"
                      className="min-h-28"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="station-note-reason">Причина изменения</Label>
                    <Textarea
                      id="station-note-reason"
                      value={noteReason}
                      maxLength={500}
                      onChange={(event) => setNoteReason(event.target.value)}
                      placeholder="уточнили владельца станции"
                      className="min-h-20"
                    />
                  </div>
                  <Button
                    type="button"
                    className="w-full"
                    disabled={updateNote.isPending || noteDraft.length > 2000 || !noteReason.trim()}
                    onClick={() => updateNote.mutate()}
                  >
                    <Pencil className="h-4 w-4" />
                    Сохранить комментарий
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <section className="rounded-lg border bg-card p-4">
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
                    lifecycleToneClass(lifecycleDisplay.tone)
                  )}
                >
                  {lifecycleDisplay.requiresReenroll ? (
                    <RefreshCw className="h-5 w-5" />
                  ) : lifecycleDisplay.tone === 'blocked' ? (
                    <Ban className="h-5 w-5" />
                  ) : lifecycleDisplay.tone === 'warning' ? (
                    <AlertTriangle className="h-5 w-5" />
                  ) : (
                    <CheckCircle2 className="h-5 w-5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-medium">{lifecycleDisplay.title}</h2>
                    <Badge variant={lifecycleBadgeVariant(lifecycleDisplay.tone)}>
                      {lifecycleDisplay.badge}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{lifecycleDisplay.detail}</p>
                </div>
              </div>
            </section>

            <section className="rounded-lg border bg-card p-4">
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
                    licenseToneClass(licenseDisplay.tone)
                  )}
                >
                  {licenseState.isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : licenseDisplay.pendingRefresh ? (
                    <RefreshCw className="h-5 w-5" />
                  ) : licenseDisplay.overrideApplied ? (
                    <ShieldCheck className="h-5 w-5" />
                  ) : licenseData?.effectiveAllowed ? (
                    <CheckCircle2 className="h-5 w-5" />
                  ) : (
                    <Ban className="h-5 w-5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-medium">{licenseDisplay.title}</h2>
                    <Badge variant={licenseBadgeVariant(licenseDisplay.tone)}>
                      {licenseDisplay.badge}
                    </Badge>
                  </div>
                  {licenseData && (
                    <p className="mt-1 text-sm text-muted-foreground">
                      {licenseDisplay.detail} · {formatDateTime(licenseData.evaluatedAt)}
                    </p>
                  )}
                </div>
              </div>

              {licenseDisplay.pendingRefresh && (
                <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                  <div className="flex items-center gap-2 font-medium">
                    <RefreshCw className="h-4 w-4" />
                    Ожидает refresh установки
                  </div>
                  <p className="mt-1">
                    Сервер уже разрешил работу, но локальный сертификат обновится после следующего refresh.
                  </p>
                </div>
              )}

              {licenseData?.override && (
                <div className="mt-3 rounded-md bg-muted p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium">
                    <Clock className="h-4 w-4" />
                    Override до {formatDateTime(licenseData.override.validUntil)}
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    Выдал: {licenseData.override.issuedByEmployeeId || 'не указано'} · статус {licenseData.override.status}
                  </p>
                </div>
              )}

              {licenseData && (
                <dl className="mt-3 grid gap-3 text-sm">
                  <InfoRow label="Entitlement" value={licenseData.entitlementStatus || 'нет статуса'} />
                  <InfoRow label="Сертификат содержит override" value={licenseData.certCarriesActiveOverride ? 'да' : 'нет'} />
                  <InfoRow label="Нужен refresh" value={licenseData.requiresRefresh ? 'да' : 'нет'} />
                  {licenseData.cert && (
                    <InfoRow
                      label="Сертификат до"
                      value={`${formatDateTime(licenseData.cert.notAfter)}${licenseData.cert.expired ? ' · истек' : ''}`}
                    />
                  )}
                </dl>
              )}
            </section>

            <section className="rounded-lg border bg-card p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                  <History className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-medium">История станции</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Audit, overrides и сертификаты через ds-main visibility.
                  </p>
                </div>
              </div>

              <div className="mt-4 grid gap-4">
                <AuditTimeline
                  items={audit.data?.items}
                  isLoading={audit.isLoading}
                  isError={audit.isError}
                />
                <OverrideHistory
                  items={overrides.data?.items}
                  isLoading={overrides.isLoading}
                  isError={overrides.isError}
                />
                <CertHistory
                  items={certs.data?.items}
                  isLoading={certs.isLoading}
                  isError={certs.isError}
                />
              </div>
            </section>

            {!stationData.decommissionedAt && (
              <section className="rounded-lg border bg-card p-4 space-y-3">
                <div>
                  <h2 className="font-medium">Разрешить на 24 часа</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Доступно один раз за 30 дней для этой станции.
                  </p>
                </div>
                <Textarea value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)} />
                <Button
                  type="button"
                  className="w-full"
                  disabled={override.isPending || !overrideReason.trim()}
                  onClick={() => override.mutate()}
                >
                  <KeyRound className="h-4 w-4" />
                  Выдать разрешение
                </Button>
              </section>
            )}

            {!stationData.decommissionedAt && (
              <section className="rounded-lg border bg-card p-4 space-y-3">
                <div>
                  <h2 className="font-medium">Вывести и потребовать re-enroll</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    После вывода установка должна пройти новую регистрацию.
                  </p>
                </div>
                <Textarea
                  value={decommissionReason}
                  onChange={(event) => setDecommissionReason(event.target.value)}
                  placeholder="Причина вывода"
                />
                <Button
                  type="button"
                  variant="destructive"
                  className="w-full"
                  disabled={decommission.isPending || !decommissionReason.trim()}
                  onClick={() => {
                    if (window.confirm('Вывести эту станцию из эксплуатации?')) {
                      decommission.mutate();
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Вывести станцию
                </Button>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function clientDisplay(clientId: number, clientName?: string): string {
  const name = clientName?.trim();
  if (!name) {
    return `Клиент ${clientId}`;
  }
  return `${name} [${clientId}]`;
}

function InfoRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid gap-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn('break-words', mono && 'font-mono text-xs')}>{value}</dd>
    </div>
  );
}

function AuditTimeline({
  items,
  isLoading,
  isError,
}: {
  items?: StationAuditRecord[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <div>
      <SectionTitle title="Audit" />
      {isLoading && <HistoryLoading />}
      {isError && <HistoryError label="audit" />}
      {!isLoading && !isError && (!items || items.length === 0) && <EmptyHistory label="Событий пока нет" />}
      {!isLoading && !isError && items && items.length > 0 && (
        <div className="mt-2 divide-y rounded-md border">
          {items.slice(0, 8).map((entry) => {
            const preview = stationAuditPayloadPreview(entry);
            return (
              <div key={entry.id} className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{stationAuditEventLabel(entry.event)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{formatDateTime(entry.at)}</p>
                  </div>
                  {entry.initiatedByEmployeeId && (
                    <Badge variant="outline" className="shrink-0">
                      {entry.initiatedByEmployeeId}
                    </Badge>
                  )}
                </div>
                {preview && <p className="mt-2 break-words text-xs text-muted-foreground">{preview}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OverrideHistory({
  items,
  isLoading,
  isError,
}: {
  items?: StationOverrideRecord[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <div>
      <SectionTitle title="Overrides" />
      {isLoading && <HistoryLoading />}
      {isError && <HistoryError label="overrides" />}
      {!isLoading && !isError && (!items || items.length === 0) && <EmptyHistory label="Override history пустая" />}
      {!isLoading && !isError && items && items.length > 0 && (
        <div className="mt-2 divide-y rounded-md border">
          {items.slice(0, 5).map((override) => (
            <div key={override.id} className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium">Override до {formatDateTime(override.validUntil)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Выдал: {override.issuedByEmployeeId || 'не указано'} · {formatDateTime(override.issuedAt)}
                  </p>
                </div>
                <Badge variant={override.status === 'ACTIVE' ? 'visit' : 'outline'} className="shrink-0">
                  {override.status}
                </Badge>
              </div>
              <p className="mt-2 break-words text-xs text-muted-foreground">{override.reason}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CertHistory({
  items,
  isLoading,
  isError,
}: {
  items?: StationCertRecord[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <div>
      <SectionTitle title="Сертификаты" icon={<FileText className="h-4 w-4" />} />
      {isLoading && <HistoryLoading />}
      {isError && <HistoryError label="certs" />}
      {!isLoading && !isError && (!items || items.length === 0) && <EmptyHistory label="Сертификатов пока нет" />}
      {!isLoading && !isError && items && items.length > 0 && (
        <div className="mt-2 divide-y rounded-md border">
          {items.slice(0, 5).map((cert) => (
            <div key={cert.id} className="p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="break-all font-mono text-xs">{cert.id}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Выдан: {formatDateTime(cert.issuedAt)} · до {formatDateTime(cert.notAfter)}
                  </p>
                </div>
                <Badge variant={cert.revokedAt ? 'deadline' : 'outline'} className="shrink-0">
                  key v{cert.signingKeyVersion}
                </Badge>
              </div>
              {cert.revokedAt && (
                <p className="mt-2 text-xs text-destructive">
                  Отозван: {formatDateTime(cert.revokedAt)} {cert.revokedReason ? `· ${cert.revokedReason}` : ''}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTitle({ title, icon }: { title: string; icon?: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm font-medium">
      {icon}
      {title}
    </div>
  );
}

function HistoryLoading() {
  return (
    <div className="mt-2 space-y-2">
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  );
}

function HistoryError({ label }: { label: string }) {
  return <p className="mt-2 text-sm text-destructive">Не удалось загрузить {label}</p>;
}

function EmptyHistory({ label }: { label: string }) {
  return <p className="mt-2 text-sm text-muted-foreground">{label}</p>;
}

function lifecycleToneClass(tone: 'allowed' | 'blocked' | 'warning' | 'neutral'): string {
  switch (tone) {
    case 'allowed':
      return 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300';
    case 'blocked':
      return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300';
    case 'warning':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function lifecycleBadgeVariant(tone: 'allowed' | 'blocked' | 'warning' | 'neutral') {
  switch (tone) {
    case 'allowed':
      return 'visit';
    case 'blocked':
      return 'deadline';
    case 'warning':
      return 'today';
    default:
      return 'outline';
  }
}

function licenseToneClass(tone: 'allowed' | 'blocked' | 'pending' | 'neutral'): string {
  switch (tone) {
    case 'allowed':
      return 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300';
    case 'blocked':
      return 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300';
    case 'pending':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function licenseBadgeVariant(tone: 'allowed' | 'blocked' | 'pending' | 'neutral') {
  switch (tone) {
    case 'allowed':
      return 'visit';
    case 'blocked':
      return 'deadline';
    case 'pending':
      return 'today';
    default:
      return 'outline';
  }
}
